import React, { useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  alpha,
  useTheme,
  IconButton,
  Avatar,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import { useGetTeamQuery, useGetPositionQuery } from "../../../Api/attoDeskApi";
import { ITeam, IPosition } from "../../../Api/Interface/api.interface";
import LoadingPage from "../../../Components/LoadingDialog/LoadingPage";
import EditStaff from "./index";

const EditPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();

  const { data, isLoading } = useGetTeamQuery({ searchText: "", id: String(id) });
  const { data: positionData } = useGetPositionQuery({ searchText: "" });

  const staffData = useMemo(() => {
    if (data && Array.isArray(data.data) && data.data.length > 0) return data.data[0] as ITeam;
    return null;
  }, [data]);

  const positionName = useMemo(() => {
    if (!staffData?.teamPositionId || !positionData?.data) return null;
    const list = (positionData.data ?? []) as IPosition[];
    return list.find((p) => p.id === staffData.teamPositionId)?.positionName || null;
  }, [staffData, positionData]);

  if (!id) return <Typography sx={{ p: 4 }}>Staff ID missing.</Typography>;
  if (isLoading) return <LoadingPage open />;

  const fullName = staffData
    ? `${staffData.teamFirstName || ""} ${staffData.middle_name || ""} ${staffData.teamLastName || ""}`.trim()
    : "Staff Member";
  const initials = staffData
    ? `${(staffData.teamFirstName || "").charAt(0)}${(staffData.teamLastName || "").charAt(0)}`.toUpperCase()
    : "S";

  return (
    <Box>
      {/* ── Header ── */}
      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "14px",
          border: "1px solid rgba(0,0,0,0.06)",
          boxShadow: "0 1px 3px rgba(0,0,0,0.03)",
          px: { xs: 2, md: 3 },
          py: 2,
          mb: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 2,
          flexWrap: { xs: "wrap", md: "nowrap" },
        }}
      >
        {/* Back button */}
        <IconButton
          size="small"
          onClick={() => navigate(`/team-history/${id}`)}
          sx={{
            border: "1px solid rgba(0,0,0,0.08)",
            width: 36,
            height: 36,
            flexShrink: 0,
            "&:hover": { bgcolor: "rgba(0,0,0,0.03)" },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 18 }} />
        </IconButton>

        {/* Avatar + Info */}
        <Avatar
          src={staffData?.teamImg || undefined}
          sx={{
            width: 44,
            height: 44,
            fontSize: 16,
            fontWeight: 700,
            background: staffData?.teamImg
              ? undefined
              : `linear-gradient(135deg, ${theme.palette.primary.main}, ${alpha(theme.palette.primary.main, 0.6)})`,
            color: "#fff",
            flexShrink: 0,
          }}
        >
          {staffData?.teamImg ? null : initials}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 700, letterSpacing: "-0.02em" }}>
              {fullName}
            </Typography>
            {positionName && (
              <Chip
                icon={<WorkOutlineIcon sx={{ fontSize: "12px !important" }} />}
                label={positionName}
                size="small"
                sx={{
                  height: 22,
                  fontSize: 10.5,
                  fontWeight: 600,
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                  color: theme.palette.primary.main,
                }}
              />
            )}
          </Box>
          <Typography sx={{ fontSize: 12, color: "text.secondary", mt: 0.15 }}>
            Edit staff profile, contact, and schedule information
          </Typography>
        </Box>

        {/* Action */}
        <Button
          variant="outlined"
          size="small"
          onClick={() => navigate(`/team-history/${id}`)}
          sx={{
            textTransform: "none",
            fontSize: 12,
            borderColor: "rgba(0,0,0,0.12)",
            color: "text.secondary",
            height: 34,
            flexShrink: 0,
          }}
        >
          Cancel
        </Button>
      </Box>

      {/* ── Form ── */}
      <EditStaff studentId={id} />
    </Box>
  );
};

export default EditPage;
